import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Icon,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  ChevronDownIcon,
  EditIcon,
  CopyIcon,
} from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import payrollService, {
  formatPayrollCurrency,
} from "../modules/payroll/payrollService";
import { employeeService } from "../modules/employee/employeeService";

const DEFAULT_META = {
  money: {
    currency: "INR",
    storageUnit: "paise",
    displayUnit: "rupees",
    scale: 100,
  },
};

const getCalculationLabel = (component) => {
  if (component.calculationType === "FORMULA") {
    return component.formula || "-";
  }

  if (component.calculationType === "FIXED") {
    return formatPayrollCurrency(component.value || 0, DEFAULT_META);
  }

  return `${component.value || 0}%`;
};

// Helper to convert components to chart data
const getChartData = (components, type = "EARNING", ctc = 0) => {
  const filtered = (components || []).filter((c) => c.type === type);
  return filtered
    .map((c) => {
      let value = 0;
      if (c.calculationType === "FIXED") {
        value = (c.value || 0) / 100; // Convert paise to rupees
      } else if (c.calculationType === "PERCENTAGE" && ctc) {
        // Calculate percentage of CTC
        value = (ctc * (c.value || 0)) / 100;
      }
      // Skip FORMULA components (can't visualize without evaluation)
      return {
        name: c.key,
        value,
        component: c,
      };
    })
    .filter((item) => item.value > 0); // Only include components with positive values
};

// Helper to calculate component percentage of total
const getComponentPercentage = (component, components, ctc) => {
  if (component.calculationType === "FIXED" && ctc) {
    const value = (component.value || 0) / 100; // Convert paise to rupees
    return ((value / ctc) * 100).toFixed(1);
  }
  if (component.calculationType === "PERCENTAGE") {
    return (component.value || 0).toFixed(1);
  }
  // For FORMULA components, can't calculate without evaluation
  return "—";
};

const SalaryStructureDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isAssignOpen, onOpen: onAssignOpen, onClose: onAssignClose } = useDisclosure();

  const [meta, setMeta] = useState(DEFAULT_META);
  const [loading, setLoading] = useState(true);
  const [structure, setStructure] = useState(null);
  const [compensations, setCompensations] = useState([]);
  
  // Assign employee modal state
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [baseSalary, setBaseSalary] = useState("");
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  // Load available employees when opening assign modal
  const handleOpenAssignModal = async () => {
    try {
      setLoadingEmployees(true);
      const employees = await employeeService.getAllEmployees();
      
      // Filter out employees already assigned to this structure
      const assignedEmployeeIds = compensations.map((c) => c.userId?._id);
      const availableEmps = employees.filter(
        (emp) => !assignedEmployeeIds.includes(emp._id),
      );
      
      setAvailableEmployees(availableEmps);
      setSelectedEmployeeId("");
      setBaseSalary("");
      onAssignOpen();
    } catch (error) {
      toast({
        title: "Failed to load employees",
        description: error?.message || "Unable to fetch employee list",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Handle assignment submission
  const handleAssignEmployee = async () => {
    if (!selectedEmployeeId || !baseSalary) {
      toast({
        title: "Missing fields",
        description: "Please select an employee and enter a base salary",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsAssigning(true);
      
      // Convert base salary (rupees) to paise for API
      const baseSalaryPaise = Math.round(parseFloat(baseSalary) * meta.money.scale);
      
      const payload = {
        userId: selectedEmployeeId,
        salaryStructureId: id,
        baseSalary: baseSalaryPaise,
        isActive: true,
      };
      
      await payrollService.assignCompensation(payload);
      
      // Refresh compensations list
      const compensationResult = await payrollService.listCompensations({
        salaryStructureId: id,
      });
      setCompensations(compensationResult.data || []);
      
      toast({
        title: "Employee assigned successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      onAssignClose();
    } catch (error) {
      toast({
        title: "Failed to assign employee",
        description: error?.response?.data?.message || error.message || "Unknown error",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsAssigning(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [structureResult, compensationResult] = await Promise.all([
          payrollService.getSalaryStructure(id),
          payrollService.listCompensations({ salaryStructureId: id }),
        ]);

        setStructure(structureResult.data);
        setCompensations(compensationResult.data || []);
        setMeta(
          structureResult.meta || compensationResult.meta || DEFAULT_META,
        );
      } catch (error) {
        toast({
          title: "Unable to load salary structure",
          description:
            error?.response?.data?.message || error.message || "Unknown error",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, toast]);

  const stats = useMemo(() => {
    const components = structure?.components || [];
    const earnings = components.filter((item) => item.type === "EARNING");
    const deductions = components.filter((item) => item.type === "DEDUCTION");
    const activeAssignments = compensations.filter((item) => item.isActive);

    return {
      earningsCount: earnings.length,
      deductionsCount: deductions.length,
      activeAssignments: activeAssignments.length,
      totalAssignments: compensations.length,
    };
  }, [structure, compensations]);

  if (loading) {
    return (
      <VStack py={16} spacing={4}>
        <Spinner size="xl" color="blue.500" />
        <Text color="gray.500">Loading salary structure...</Text>
      </VStack>
    );
  }

  if (!structure) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <Stack spacing={4}>
          <Button
            alignSelf="flex-start"
            leftIcon={<ArrowBackIcon />}
            variant="outline"
            onClick={() => navigate("/payroll/ctc")}
          >
            Back to Payroll
          </Button>
          <Card>
            <CardBody>
              <Text color="gray.500">Salary structure not found.</Text>
            </CardBody>
          </Card>
        </Stack>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }} bg="gray.50" minH="100vh">
      <Stack spacing={4} maxW="100%" mx="auto">
        {/* ===== HEADER SECTION ===== */}
        <Stack
          spacing={{ base: 3, md: 0 }}
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          pb={{ base: 3, md: 4 }}
          borderBottom="1px"
          borderColor="gray.200"
        >
          <Stack spacing={2}>
            <HStack spacing={3} align="flex-start">
              <Button
                leftIcon={<ArrowBackIcon />}
                variant="outline"
                size={{ base: "sm", md: "md" }}
                onClick={() => navigate("/payroll/ctc")}
              >
                Back
              </Button>
              <Box>
                <Heading size={{ base: "md", md: "lg" }}>
                  {structure.name}
                </Heading>
                <Text
                  fontSize={{ base: "xs", md: "sm" }}
                  color="gray.600"
                  mt={1}
                >
                  Effective from{" "}
                  {new Date(structure.effectiveFrom).toLocaleDateString()}
                </Text>
              </Box>
              <Badge
                colorScheme={structure.isActive ? "green" : "gray"}
                h="fit-content"
              >
                {structure.isActive ? "Active" : "Inactive"}
              </Badge>
            </HStack>
          </Stack>

          {/* Actions */}
          <HStack spacing={{ base: 2, md: 3 }}>
            <Button
              leftIcon={<EditIcon />}
              colorScheme="blue"
              size={{ base: "sm", md: "md" }}
              onClick={() => toast({ title: "Edit structure (coming soon)" })}
            >
              Edit
            </Button>
            <Button
              leftIcon={<CopyIcon />}
              variant="outline"
              size={{ base: "sm", md: "md" }}
              onClick={() =>
                toast({ title: "Duplicate structure (coming soon)" })
              }
            >
              Duplicate
            </Button>
          </HStack>
        </Stack>

        {/* ===== SALARY SNAPSHOT (HERO) ===== */}
        <Card bg="white" boxShadow="md">
          <CardBody py={{ base: 4, md: 5 }}>
            <Grid
              templateColumns={{ base: "1fr", md: "1fr 1fr" }}
              gap={{ base: 2, md: 6 }}
              align="start"
            >
              {/* Left: CTC & Annual */}
              <Stack spacing={3}>
                <Box>
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    textTransform="uppercase"
                    fontWeight="bold"
                    mb={2}
                  >
                    Monthly CTC
                  </Text>
                  <Heading
                    size="2xl"
                    color="blue.600"
                    fontSize={{ base: "2xl", md: "3xl" }}
                  >
                    {formatPayrollCurrency(structure.ctc, meta)}
                  </Heading>
                </Box>
                <Box>
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    textTransform="uppercase"
                    fontWeight="bold"
                    mb={1}
                  >
                    Annual CTC
                  </Text>
                  <Text
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="600"
                    color="gray.800"
                  >
                    {formatPayrollCurrency((structure.ctc || 0) * 12, meta)}
                  </Text>
                </Box>
              </Stack>

              {/* Right: Chart */}
              <Box h={{ base: "280px", md: "300px" }} w="100%" display="flex" alignItems="center" justifyContent="center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Pie
                      data={getChartData(
                        structure.components,
                        "EARNING",
                        structure.ctc,
                      ).filter((d) => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name }) => `${name}`}
                      outerRadius={55}
                      fill="#8884d8"
                      dataKey="value"
                      isAnimationActive={false}
                    >
                      {[
                        "#3182ce",
                        "#38a169",
                        "#ed8936",
                        "#9f7aea",
                        "#38b2ac",
                      ].map((color, idx) => (
                        <Cell key={`cell-${idx}`} fill={color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) =>
                        formatPayrollCurrency(value * 100, meta)
                      }
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "8px",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Grid>

            {/* Salary Breakdown Details */}
            <Divider my={3} />
            <Stack spacing={2}>
              <Text fontSize="sm" fontWeight="bold" color="gray.700">
                Salary Split
              </Text>
              {getChartData(structure.components, "EARNING", structure.ctc).length > 0 ? (
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                  {getChartData(structure.components, "EARNING", structure.ctc).map(
                    (item, idx) => (
                      <Box key={item.name} p={2} bg="gray.50" borderRadius="md">
                        <HStack justify="space-between" mb={1}>
                          <Text fontSize="xs" fontWeight="600">
                            {item.name}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {getComponentPercentage(
                              item.component,
                              structure.components,
                              structure.ctc,
                            )}
                            {item.component.calculationType !== "FORMULA" && "%"}
                          </Text>
                        </HStack>
                        <Text fontSize="sm" fontWeight="bold" color="blue.600">
                          {formatPayrollCurrency(item.value * 100, meta)}
                        </Text>
                      </Box>
                    )
                  )}
                </SimpleGrid>
              ) : (
                <Text color="gray.500" fontSize="sm">No earnings components to display</Text>
              )}
            </Stack>
          </CardBody>
        </Card>

        {/* ===== KEY METRICS ===== */}
        <Grid
          templateColumns={{
            base: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          }}
          gap={{ base: 2, md: 3 }}
        >
          <Card bg="white" boxShadow="sm">
            <CardBody py={{ base: 3, md: 4 }}>
              <Stat>
                <StatLabel fontSize={{ base: "xs", md: "sm" }}>
                  Earning Components
                </StatLabel>
                <StatNumber
                  fontSize={{ base: "2xl", md: "3xl" }}
                  color="green.600"
                >
                  {
                    (structure.components || []).filter(
                      (c) => c.type === "EARNING",
                    ).length
                  }
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card bg="white" boxShadow="sm">
            <CardBody py={{ base: 3, md: 4 }}>
              <Stat>
                <StatLabel fontSize={{ base: "xs", md: "sm" }}>
                  Deductions
                </StatLabel>
                <StatNumber
                  fontSize={{ base: "2xl", md: "3xl" }}
                  color="red.600"
                >
                  {
                    (structure.components || []).filter(
                      (c) => c.type === "DEDUCTION",
                    ).length
                  }
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card bg="white" boxShadow="sm">
            <CardBody py={{ base: 3, md: 4 }}>
              <Stat>
                <StatLabel fontSize={{ base: "xs", md: "sm" }}>
                  PF Applicable
                </StatLabel>
                <StatNumber fontSize={{ base: "2xl", md: "3xl" }}>
                  <Badge
                    colorScheme={
                      (structure.components || []).some((c) => c.isPFApplicable)
                        ? "green"
                        : "gray"
                    }
                  >
                    {(structure.components || []).some((c) => c.isPFApplicable)
                      ? "Yes"
                      : "No"}
                  </Badge>
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card bg="white" boxShadow="sm">
            <CardBody py={{ base: 3, md: 4 }}>
              <Stat>
                <StatLabel fontSize={{ base: "xs", md: "sm" }}>
                  ESI Applicable
                </StatLabel>
                <StatNumber fontSize={{ base: "2xl", md: "3xl" }}>
                  <Badge
                    colorScheme={
                      (structure.components || []).some(
                        (c) => c.isESIApplicable,
                      )
                        ? "green"
                        : "gray"
                    }
                  >
                    {(structure.components || []).some((c) => c.isESIApplicable)
                      ? "Yes"
                      : "No"}
                  </Badge>
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </Grid>

        {/* ===== EARNINGS BREAKDOWN ===== */}
        <Card bg="white">
          <CardHeader pb={3}>
            <HStack justify="space-between" align="center">
              <Heading size={{ base: "sm", md: "md" }}>
                Earnings Breakdown
              </Heading>
              <Badge colorScheme="green">
                {
                  (structure.components || []).filter(
                    (c) => c.type === "EARNING",
                  ).length
                }{" "}
                Components
              </Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            <TableContainer
              overflowX={{ base: "auto", md: "visible" }}
              sx={{
                "&::-webkit-scrollbar": { height: "4px" },
                "&::-webkit-scrollbar-track": { bg: "gray.100" },
                "&::-webkit-scrollbar-thumb": {
                  bg: "gray.400",
                  borderRadius: "2px",
                },
              }}
            >
              <Table
                size={{ base: "xs", md: "sm" }}
                minW={{ base: "600px", md: "100%" }}
              >
                <Thead>
                  <Tr bg="blue.50">
                    <Th fontSize={{ base: "xs", md: "sm" }}>Component</Th>
                    <Th fontSize={{ base: "xs", md: "sm" }} isNumeric>
                      Amount
                    </Th>
                    <Th fontSize={{ base: "xs", md: "sm" }} isNumeric>
                      % of CTC
                    </Th>
                    <Th fontSize={{ base: "xs", md: "sm" }}>Taxable</Th>
                    <Th fontSize={{ base: "xs", md: "sm" }} textAlign="center">
                      PF
                    </Th>
                    <Th fontSize={{ base: "xs", md: "sm" }} textAlign="center">
                      ESI
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(structure.components || [])
                    .filter((c) => c.type === "EARNING")
                    .map((component, idx) => (
                      <Tr
                        key={component.key}
                        bg={idx % 2 === 0 ? "white" : "gray.50"}
                      >
                        <Td
                          fontWeight="600"
                          fontSize={{ base: "xs", md: "sm" }}
                        >
                          {component.key}
                        </Td>
                        <Td
                          isNumeric
                          fontSize={{ base: "xs", md: "sm" }}
                          fontWeight="500"
                        >
                          {getCalculationLabel(component)}
                        </Td>
                        <Td isNumeric fontSize={{ base: "xs", md: "sm" }}>
                          {getComponentPercentage(
                            component,
                            structure.components,
                            structure.ctc,
                          )}
                          {component.calculationType !== "FORMULA" && "%"}
                        </Td>
                        <Td fontSize={{ base: "xs", md: "sm" }}>
                          <Badge
                            colorScheme={component.isTaxable ? "blue" : "gray"}
                          >
                            {component.isTaxable ? "Yes" : "No"}
                          </Badge>
                        </Td>
                        <Td textAlign="center">
                          {component.isPFApplicable ? (
                            <Badge colorScheme="green" fontSize="xs">
                              ✓
                            </Badge>
                          ) : (
                            <Text fontSize="xs" color="gray.400">
                              —
                            </Text>
                          )}
                        </Td>
                        <Td textAlign="center">
                          {component.isESIApplicable ? (
                            <Badge colorScheme="green" fontSize="xs">
                              ✓
                            </Badge>
                          ) : (
                            <Text fontSize="xs" color="gray.400">
                              —
                            </Text>
                          )}
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
        )}

        {/* ===== DEDUCTIONS BREAKDOWN ===== */}
        {(structure.components || []).filter((c) => c.type === "DEDUCTION")
          .length > 0 ? (
          <Card bg="white">
            <CardHeader pb={3}>
              <HStack justify="space-between" align="center">
                <Heading size={{ base: "sm", md: "md" }}>Deductions</Heading>
                <Badge colorScheme="red">
                  {
                    (structure.components || []).filter(
                      (c) => c.type === "DEDUCTION",
                    ).length
                  }{" "}
                  Components
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <TableContainer
                overflowX={{ base: "auto", md: "visible" }}
                sx={{
                  "&::-webkit-scrollbar": { height: "4px" },
                  "&::-webkit-scrollbar-track": { bg: "gray.100" },
                  "&::-webkit-scrollbar-thumb": {
                    bg: "gray.400",
                    borderRadius: "2px",
                  },
                }}
              >
                <Table
                  size={{ base: "xs", md: "sm" }}
                  minW={{ base: "600px", md: "100%" }}
                >
                  <Thead>
                    <Tr bg="red.50">
                      <Th fontSize={{ base: "xs", md: "sm" }}>Component</Th>
                      <Th fontSize={{ base: "xs", md: "sm" }} isNumeric>
                        Amount
                      </Th>
                      <Th fontSize={{ base: "xs", md: "sm" }} isNumeric>
                        % of CTC
                      </Th>
                      <Th fontSize={{ base: "xs", md: "sm" }}>Taxable</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(structure.components || [])
                      .filter((c) => c.type === "DEDUCTION")
                      .map((component, idx) => (
                        <Tr
                          key={component.key}
                          bg={idx % 2 === 0 ? "white" : "gray.50"}
                        >
                          <Td
                            fontWeight="600"
                            fontSize={{ base: "xs", md: "sm" }}
                          >
                            {component.key}
                          </Td>
                          <Td
                            isNumeric
                            fontSize={{ base: "xs", md: "sm" }}
                            fontWeight="500"
                          >
                            {getCalculationLabel(component)}
                          </Td>
                          <Td isNumeric fontSize={{ base: "xs", md: "sm" }}>
                            {getComponentPercentage(
                              component,
                              structure.components,
                              structure.ctc,
                            )}
                            {component.calculationType !== "FORMULA" && "%"}
                          </Td>
                          <Td fontSize={{ base: "xs", md: "sm" }}>
                            <Badge
                              colorScheme={
                                component.isTaxable ? "blue" : "gray"
                              }
                            >
                              {component.isTaxable ? "Yes" : "No"}
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>
        ) : null}

        {/* ===== EMPLOYEE ASSIGNMENTS ===== */}
        <Card bg="white">
          <CardHeader pb={3}>
            <HStack justify="space-between" align="center">
              <Stack spacing={1}>
                <Heading size={{ base: "sm", md: "md" }}>
                  Employee Assignments
                </Heading>
                <Text fontSize="sm" color="gray.600">
                  {stats.activeAssignments} active out of{" "}
                  {stats.totalAssignments} total
                </Text>
              </Stack>
              <Button
                colorScheme="blue"
                size={{ base: "xs", md: "sm" }}
                onClick={handleOpenAssignModal}
              >
                + Assign Employee
              </Button>
            </HStack>
          </CardHeader>
          <CardBody>
            {compensations.length === 0 ? (
              <Text color="gray.500" fontSize={{ base: "sm", md: "md" }} py={6}>
                No employee compensation assignments yet. Assign employees to
                use this structure.
              </Text>
            ) : (
              <TableContainer
                overflowX={{ base: "auto", md: "visible" }}
                sx={{
                  "&::-webkit-scrollbar": { height: "4px" },
                  "&::-webkit-scrollbar-track": { bg: "gray.100" },
                  "&::-webkit-scrollbar-thumb": {
                    bg: "gray.400",
                    borderRadius: "2px",
                  },
                }}
              >
                <Table
                  size={{ base: "xs", md: "sm" }}
                  minW={{ base: "600px", md: "100%" }}
                >
                  <Thead>
                    <Tr bg="gray.50">
                      <Th fontSize={{ base: "xs", md: "sm" }}>Employee</Th>
                      <Th fontSize={{ base: "xs", md: "sm" }}>Employee ID</Th>
                      <Th fontSize={{ base: "xs", md: "sm" }} isNumeric>
                        Base Salary
                      </Th>
                      <Th fontSize={{ base: "xs", md: "sm" }}>
                        Effective From
                      </Th>
                      <Th fontSize={{ base: "xs", md: "sm" }}>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {compensations.map((record, idx) => (
                      <Tr
                        key={record._id}
                        bg={idx % 2 === 0 ? "white" : "gray.50"}
                        cursor="pointer"
                        _hover={{ bg: "blue.50" }}
                        onClick={() =>
                          navigate(`/payroll/compensations/${record._id}`)
                        }
                      >
                        <Td>
                          <Stack spacing={0}>
                            <Text
                              fontWeight="600"
                              fontSize={{ base: "xs", md: "sm" }}
                            >
                              {record.userId?.name || "-"}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {record.userId?.email || "-"}
                            </Text>
                          </Stack>
                        </Td>
                        <Td fontSize={{ base: "xs", md: "sm" }}>
                          {record.userId?.employeeId || "-"}
                        </Td>
                        <Td
                          isNumeric
                          fontSize={{ base: "xs", md: "sm" }}
                          fontWeight="500"
                        >
                          {formatPayrollCurrency(record.baseSalary, meta)}
                        </Td>
                        <Td fontSize={{ base: "xs", md: "sm" }}>
                          {new Date(record.effectiveFrom).toLocaleDateString()}
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={record.isActive ? "green" : "gray"}
                            fontSize="xs"
                          >
                            {record.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </CardBody>
        </Card>

        {/* ===== METADATA (COLLAPSED) ===== */}
        <Accordion allowToggle>
          <AccordionItem
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
          >
            <AccordionButton py={3}>
              <Box
                as="span"
                flex="1"
                textAlign="left"
                fontSize={{ base: "sm", md: "md" }}
                fontWeight="600"
              >
                View Details
              </Box>
              <ChevronDownIcon />
            </AccordionButton>
            <AccordionPanel pb={4} pt={3}>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gap={4}
              >
                <Box>
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    textTransform="uppercase"
                    fontWeight="bold"
                    mb={1}
                  >
                    Structure Code
                  </Text>
                  <Text fontWeight="600">{structure.code}</Text>
                </Box>
                <Box>
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    textTransform="uppercase"
                    fontWeight="bold"
                    mb={1}
                  >
                    Created
                  </Text>
                  <Text fontWeight="600">
                    {structure.createdAt
                      ? new Date(structure.createdAt).toLocaleString()
                      : "-"}
                  </Text>
                </Box>
                <Box>
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    textTransform="uppercase"
                    fontWeight="bold"
                    mb={1}
                  >
                    Last Updated
                  </Text>
                  <Text fontWeight="600">
                    {structure.updatedAt
                      ? new Date(structure.updatedAt).toLocaleString()
                      : "-"}
                  </Text>
                </Box>
              </Grid>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>

        {/* ===== ASSIGN EMPLOYEE MODAL ===== */}
        <Modal isOpen={isAssignOpen} onClose={onAssignClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Assign Employee to Structure</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Select Employee
                  </FormLabel>
                  {loadingEmployees ? (
                    <HStack spacing={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color="gray.600">
                        Loading employees...
                      </Text>
                    </HStack>
                  ) : availableEmployees.length === 0 ? (
                    <Text fontSize="sm" color="gray.600">
                      All available employees are already assigned to this structure.
                    </Text>
                  ) : (
                    <Select
                      placeholder="Choose an employee"
                      value={selectedEmployeeId}
                      onChange={(e) => setSelectedEmployeeId(e.target.value)}
                      isDisabled={isAssigning}
                    >
                      {availableEmployees.map((emp) => (
                        <option key={emp._id} value={emp._id}>
                          {emp.name} ({emp.employeeId})
                        </option>
                      ))}
                    </Select>
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Base Salary (₹)
                  </FormLabel>
                  <Input
                    type="number"
                    placeholder="Enter monthly base salary"
                    value={baseSalary}
                    onChange={(e) => setBaseSalary(e.target.value)}
                    isDisabled={isAssigning}
                    step="100"
                    min="0"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Enter the amount without decimal. All earnings and deductions will be calculated using this.
                  </Text>
                </FormControl>
              </Stack>
            </ModalBody>

            <ModalFooter>
              <HStack spacing={3}>
                <Button
                  variant="outline"
                  onClick={onAssignClose}
                  isDisabled={isAssigning}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleAssignEmployee}
                  isLoading={isAssigning}
                  loadingText="Assigning..."
                  isDisabled={!selectedEmployeeId || !baseSalary || availableEmployees.length === 0}
                >
                  Assign Employee
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Stack>
    </Box>
  );
};

export default SalaryStructureDetailPage;
